import { ChevronDown, X } from 'lucide-react';
import React from 'react';
import { ComboBox as AriaComboBox, type ComboBoxProps as AriaComboBoxProps, ListBox, type ListBoxItemProps } from 'react-aria-components';
import { Button } from '../../../components/buttons/Button';
import { FieldGroup } from '../base-controls/FieldGroup';
import { TextInput } from '../base-controls/text-controls/TextInput';
import { DropdownItem, DropdownSection, type DropdownSectionProps } from '../base-controls/ListBox';
import { Popover } from '../../../components/Popover';
import { cn } from '../../../components/utils';
import { ComboBoxStateContext } from 'react-aria-components';
import type { BaseFieldProps } from '../../../components/types';
import { FieldDescription } from '../base-controls/FieldDescription';
import { FieldError } from '../base-controls/FieldError';
import { FieldWarning } from '../base-controls/FieldWarning';
import { FieldLabelWithHtml } from '../base-controls/FieldLabelWithHtml';
import { tv } from 'tailwind-variants';
import { baseControlStyles } from '../base-controls/util';

const controlStyles = tv({
	extend: baseControlStyles,
	slots: {
		base: 'min-w-[250px]'
	}
});

export interface ComboBoxFieldProps<T extends object> extends Omit<AriaComboBoxProps<T>, 'children'>, BaseFieldProps {
	children: React.ReactNode | ((item: T) => React.ReactNode);
	placeholder?: string;
	hideChevron?: boolean;
	noOptionsMessage?: string;
}

function ComboBoxClearButton() {
	const state = React.useContext(ComboBoxStateContext);
	return (
		<Button
			slot={null}
			Icon={X}
			className="mr-1 shrink-0 outline-offset-0"
			aria-label="Clear"
			size="xs"
			appearance="outline"
			intent="neutralLight"
			onClick={() => state?.setSelectedKey(null)}
		/>
	);
}

export function ComboBoxField<T extends object>({
	label,
	description,
	warningMessage,
	children,
	items,
	hideChevron = false,
	noOptionsMessage,
	...props
}: ComboBoxFieldProps<T>) {
	const { base } = controlStyles();

	const itemsCount = Array.isArray(items) ? items.length : 0;
	return (
		<AriaComboBox {...props} className={cn(base(), props.className)} aria-label={label}>
			{!props.hideLabel ? <FieldLabelWithHtml className="mb-1" label={label} required={props.required ?? false} /> : null}
			<FieldGroup>
				<TextInput autoComplete="off" className="border-0 outline-none" placeholder={props.placeholder} />
				{!props.isReadOnly && !props.isDisabled && props.selectedKey && <ComboBoxClearButton />}
				{!hideChevron && (
					<Button
						size="xs"
						appearance="outline"
						intent="neutralLight"
						className="mr-1 shrink-0 outline-offset-0"
						Icon={ChevronDown}
						aria-label="Open"
					/>
				)}
			</FieldGroup>
			{description && <FieldDescription>{description}</FieldDescription>}
			{props.error && <FieldError {...props.error} />}
			<FieldWarning>{warningMessage}</FieldWarning>
			{(itemsCount > 0 || noOptionsMessage) && (
				<Popover className="w-[--trigger-width]">
					<ListBox
						items={items}
						renderEmptyState={
							noOptionsMessage ? () => <div className="px-3 py-2 text-sm text-gray-500">{noOptionsMessage}</div> : undefined
						}
						className="max-h-[inherit] overflow-auto p-1 outline-0 [clip-path:inset(0_0_0_0_round_.75rem)]"
					>
						{children}
					</ListBox>
				</Popover>
			)}
		</AriaComboBox>
	);
}

export function ComboBoxItem(props: ListBoxItemProps) {
	return <DropdownItem {...props} />;
}

export function ComboBoxSection<T extends object>(props: DropdownSectionProps<T>) {
	return <DropdownSection {...props} />;
}
